import { FC, useState } from 'react';
import { Maps, Timeline, useMediaQuery, variables } from '@hopskipdrive/react-components';
import { colorTokens } from '@rideiq/shared/components/variables';
import { includes } from 'lodash';

import { RIDE_STATES } from '../../../constants';
import { useRideEditor } from '../../hooks/useRideEditor';

import { CareDriver, CompletedRoute } from './CareDriver';
import { INITIAL_ZOOM_LEVEL } from './RouteMap.helpers';
import { RATimelineLocationDot, RATimelineNumberDot, RouteMapContainer } from './RouteMap.styles';
import { useMapCenter } from './useMapCenter';
import { useRouteMapDetails } from './useRouteMapDetails';

interface RouteMapViewProps {
  rideId: number;
}

const rideEndedStatuses = [...RIDE_STATES.canceled, ...RIDE_STATES.completed];

export const RouteMapView: React.FC<RouteMapViewProps> = ({ rideId }) => {
  const [mapInstance, setMapInstance] = useState<google.maps.Map>();
  const { waypointsOnMap, raWaypointOnMap, rideDetails, completeAnchorTime } = useRideEditor();
  const isDesktop = useMediaQuery({ minWidth: variables.breakpoints.desktop });
  const { routeDetails } = useRouteMapDetails({
    isMapReady: !!mapInstance,
    waypointsOnMap,
    completeAnchorTime,
  });
  const { center } = useMapCenter({ useRideEditor, mapInstance, isDesktop });
  const showMapItems = mapInstance && routeDetails && rideDetails && rideId;

  const splitLatLng = (latLng) => {
    const latLngArray = latLng?.split(',');

    if (latLngArray) {
      return { lat: parseFloat(latLngArray[0]), lng: parseFloat(latLngArray[1]) };
    }

    return null;
  };

  return (
    <RouteMapContainer>
      <Maps
        googleMapsApiKey={window.env?.RIDEIQ_GOOGLE_MAPS_API_KEY}
        handleMapInstance={(newMapInstance) => setMapInstance(newMapInstance)}
        center={center}
        zoom={INITIAL_ZOOM_LEVEL}
        height={isDesktop ? 'calc(100vh)' : 'calc(100vh - 120px)'}
        minWidth={isDesktop ? '400px' : '100%'}
        options={{
          cameraControlOptions: {
            position: 3,
          },
        }}
      >
        {raWaypointOnMap && raWaypointOnMap.type === 'ra_start' && splitLatLng(raWaypointOnMap.latLng) && (
          <Maps.Marker position={splitLatLng(raWaypointOnMap.latLng) || undefined}>
            <RATimelineLocationDot num={1} />
          </Maps.Marker>
        )}
        {waypointsOnMap
          .filter((waypoint) => waypoint.type !== 'ra_start' && waypoint.type !== 'ra_end')
          .map((waypoint, index) => {
            const latLngArray = waypoint.latLng?.split(',');
            const latLng = latLngArray ? { lat: parseFloat(latLngArray[0]), lng: parseFloat(latLngArray[1]) } : null;

            return waypoint.location && latLng ? (
              <Maps.Marker key={index} position={latLng}>
                {waypoint.type === 'pick_up' ? (
                  <Timeline.LocationDot
                    num={raWaypointOnMap && raWaypointOnMap.type === 'ra_start' ? index + 2 : index + 1}
                    data-testid={waypoint.type === 'pick_up' ? 'waypoint-pickup-marker' : 'waypoint-dropoff-marker'}
                  />
                ) : (
                  <Timeline.NumberDot
                    num={raWaypointOnMap && raWaypointOnMap.type === 'ra_start' ? index + 2 : index + 1}
                    data-testid={waypoint.type === 'pick_up' ? 'waypoint-pickup-marker' : 'waypoint-dropoff-marker'}
                  />
                )}
              </Maps.Marker>
            ) : null;
          })}
        {raWaypointOnMap && raWaypointOnMap.type === 'ra_end' && splitLatLng(raWaypointOnMap.latLng) && (
          <Maps.Marker position={splitLatLng(raWaypointOnMap.latLng) || undefined}>
            <RATimelineNumberDot
              num={
                waypointsOnMap.filter((waypoint) => waypoint.type !== 'ra_start' && waypoint.type !== 'ra_end').length +
                1
              }
            />
          </Maps.Marker>
        )}
        {showMapItems && <DriverAndPolylines rideId={rideId} routeDetails={routeDetails} rideDetails={rideDetails} />}
      </Maps>
    </RouteMapContainer>
  );
};

interface DriverAndPolylinesProps {
  rideId: number;
  routeDetails: RouteDetails;
  rideDetails: RideDetails;
}
export const DriverAndPolylines: FC<DriverAndPolylinesProps> = ({ rideId, routeDetails, rideDetails }) => {
  const [initialStatus] = useState(rideDetails.status);
  const hasRideEnded = includes(rideEndedStatuses, rideDetails.status);
  const hasTransitionedToCanceledOrComplete = hasRideEnded ? !includes(rideEndedStatuses, initialStatus) : false;
  const showActiveRoute = includes(RIDE_STATES.inProgress, rideDetails.status);

  return (
    <>
      {routeDetails && routeDetails.plannedPath && (
        <Maps.Polyline
          data-testid="planned-route-polyline"
          options={{
            path: routeDetails.plannedPath,
            geodesic: true,
            strokeColor: colorTokens.secondary.main,
            strokeOpacity: 1.0,
            strokeWeight: 3,
            zIndex: 1,
          }}
        />
      )}
      {(showActiveRoute || hasTransitionedToCanceledOrComplete) && (
        <CareDriver
          rideCoordinates={rideDetails?.coordinates}
          rideId={rideId}
          isInProgress={includes(RIDE_STATES.inProgress, rideDetails.status)}
          shouldHideCarMarker={includes([...RIDE_STATES.completed, ...RIDE_STATES.canceled], rideDetails.status)}
        />
      )}
      {hasRideEnded && !hasTransitionedToCanceledOrComplete && <CompletedRoute path={routeDetails?.actualPath} />}
    </>
  );
};
